import { lazyRouteComponent, type RouteComponent } from '@tanstack/react-router';
import {
  Blocks,
  Bot,
  Boxes,
  Eye,
  Factory,
  FlaskConical,
  FolderKanban,
  Handshake,
  LayoutGrid,
  Library,
  Server,
  Settings2,
} from 'lucide-react';
import type { WebModule } from '../web-module.ts';
import { SubscriptionSection } from './portal/Subscription.tsx';
import { TestClientBanner } from './portal/TestClientBanner.tsx';

/**
 * O módulo Smart Builder nas telas (ADR 0279): a área do cliente, a Fábrica, as telas públicas
 * (assinar, autorizar a IA, o chat embutido) e o que ele completa nas telas da base (ADR 0131 do
 * Ground Up).
 */
export const webModule: WebModule = {
  id: 'factory',
  areas: [
    {
      // Área do cliente (ADR 0211): só aparece para quem tem o perfil Cliente; a equipe não a vê
      // (as permissões são explícitas, fora do Administrador).
      id: 'portal',
      key: 'factory.areas.portal',
      icon: FolderKanban,
      groups: [
        {
          key: null,
          items: [
            {
              to: '/my-systems',
              key: 'factory.nav.mySystems',
              icon: FolderKanban,
              permission: 'portal.system.read',
            },
          ],
        },
        {
          // O que vale para a conta inteira, e não para um sistema só, fica no menu (ADR 0300): as
          // contas de IA e o GitHub da pessoa (ADR 0220).
          key: 'factory.nav.yourAccount',
          items: [
            {
              to: '/my-systems/connect-ai',
              key: 'factory.nav.connectAi',
              icon: Bot,
              permission: 'portal.ai.connect',
            },
          ],
        },
      ],
    },
    {
      // Fábrica (ADR 0200): os sistemas criados a partir do Ground Up e os servidores de destino.
      id: 'factory',
      key: 'factory.areas.factory',
      aboutKey: 'factory.departmentHome.about.factory',
      icon: Factory,
      groups: [
        {
          key: null,
          items: [
            {
              // Painel da Fábrica (ADR 0293): a abertura da área, no lugar do resumo genérico.
              to: '/factory',
              key: 'factory.nav.dashboard',
              icon: LayoutGrid,
              permission: 'factory.client.read',
            },
            {
              to: '/factory/clients',
              key: 'factory.nav.factoryClients',
              icon: Handshake,
              permission: 'factory.client.read',
            },
            {
              to: '/factory/systems',
              key: 'factory.nav.factorySystems',
              icon: Boxes,
              permission: 'factory.system.read',
            },
            {
              // O sandbox da equipe (ADR 0303): o lugar de provar mudanças sem medo.
              to: '/factory/sandbox',
              key: 'factory.nav.sandbox',
              icon: FlaskConical,
              permission: 'factory.system.read',
            },
            {
              // Ver como o cliente (ADR 0308): a área do cliente do jeito que ele vê, num lugar só.
              to: '/factory/client-view',
              key: 'factory.nav.clientView',
              icon: Eye,
              permission: 'factory.client.read',
            },
            {
              to: '/factory/module-requests',
              key: 'factory.nav.moduleRequests',
              icon: Blocks,
              permission: 'factory.system.read',
            },
            {
              // Biblioteca de módulos (ADR 0295): o catálogo, a prévia da loja, o anúncio e o preço.
              to: '/factory/modules',
              key: 'factory.nav.factoryModules',
              icon: Library,
              permission: 'factory.system.read',
            },
            {
              to: '/factory/servers',
              key: 'factory.nav.factoryServers',
              icon: Server,
              permission: 'factory.system.read',
            },
            {
              // O que vale para todos os sistemas e só a equipe muda (ADR 0291).
              to: '/factory/settings',
              key: 'factory.nav.factorySettings',
              icon: Settings2,
              permission: 'factory.server.manage',
            },
            {
              to: '/factory/connect-ai',
              key: 'factory.nav.connectAi',
              icon: Bot,
              permission: 'factory.code.ai',
            },
          ],
        },
      ],
    },
  ],
  routes: [
    {
      path: '/my-systems',
      component: lazyRouteComponent(
        () => import('./portal/MySystems.tsx'),
        'MySystemsPage',
      ) as RouteComponent,
    },
    {
      path: '/chat/$systemId',
      component: lazyRouteComponent(
        () => import('./chat/ChatPages.tsx'),
        'ChatPage',
      ) as RouteComponent,
    },
    {
      path: '/my-systems/interview/$id',
      component: lazyRouteComponent(
        () => import('./portal/Interview.tsx'),
        'InterviewPage',
      ) as RouteComponent,
    },
    {
      path: '/my-systems/connect-ai',
      component: lazyRouteComponent(
        () => import('./connect-ai/ConnectAi.tsx'),
        'ConnectAiPage',
      ) as RouteComponent,
    },
    {
      // A página de um sistema do cliente, em abas (ADR 0292).
      path: '/my-systems/$id',
      component: lazyRouteComponent(
        () => import('./portal/system/SystemPage.tsx'),
        'SystemPage',
      ) as RouteComponent,
    },
    // Endereços antigos de partes do sistema levam à aba certa (ADR 0291).
    ...(
      [
        ['modules', 'ModulesLink'],
        ['claude-code', 'ClaudeCodeLink'],
        ['settings', 'SettingsLink'],
      ] as const
    ).map(([part, name]) => ({
      path: `/my-systems/$id/${part}`,
      component: lazyRouteComponent(
        () => import('./portal/system/OldSystemLinks.tsx'),
        name,
      ) as RouteComponent,
    })),
    {
      path: '/my-systems/github',
      component: lazyRouteComponent(
        () => import('./portal/github/ClientGitHub.tsx'),
        'ClientGitHubPage',
      ) as RouteComponent,
    },
    {
      path: '/factory',
      component: lazyRouteComponent(
        () => import('./engine/dashboard/FactoryDashboard.tsx'),
        'FactoryDashboardPage',
      ) as RouteComponent,
    },
    {
      path: '/factory/sandbox',
      component: lazyRouteComponent(
        () => import('./engine/SandboxPage.tsx'),
        'SandboxPage',
      ) as RouteComponent,
    },
    {
      path: '/factory/module-requests',
      component: lazyRouteComponent(
        () => import('./engine/dashboard/ModuleRequestsPage.tsx'),
        'ModuleRequestsPage',
      ) as RouteComponent,
    },
    {
      path: '/factory/clients',
      component: lazyRouteComponent(
        () => import('./engine/Clients.tsx'),
        'FactoryClientsPage',
      ) as RouteComponent,
    },
    {
      path: '/factory/clients/$id',
      component: lazyRouteComponent(
        () => import('./engine/ClientRecord.tsx'),
        'FactoryClientRecordPage',
      ) as RouteComponent,
    },
    {
      path: '/factory/clients/$id/as-client',
      component: lazyRouteComponent(
        () => import('./engine/AsClient.tsx'),
        'FactoryAsClientPage',
      ) as RouteComponent,
    },
    {
      path: '/factory/client-view',
      component: lazyRouteComponent(
        () => import('./engine/ClientView.tsx'),
        'ClientViewPage',
      ) as RouteComponent,
    },
    {
      path: '/factory/systems/$id/as-client',
      component: lazyRouteComponent(
        () => import('./engine/SystemAsClient.tsx'),
        'SystemAsClientPage',
      ) as RouteComponent,
    },
    {
      path: '/factory/systems',
      component: lazyRouteComponent(
        () => import('./engine/Systems.tsx'),
        'FactorySystemsPage',
      ) as RouteComponent,
    },
    {
      path: '/factory/systems/$id',
      component: lazyRouteComponent(
        () => import('./engine/SystemRecord.tsx'),
        'FactorySystemRecordPage',
      ) as RouteComponent,
    },
    {
      path: '/factory/modules',
      component: lazyRouteComponent(
        () => import('./library/Library.tsx'),
        'FactoryLibraryPage',
      ) as RouteComponent,
    },
    {
      path: '/factory/modules/store',
      component: lazyRouteComponent(
        () => import('./library/StorePreview.tsx'),
        'FactoryStorePreviewPage',
      ) as RouteComponent,
    },
    {
      path: '/factory/servers',
      component: lazyRouteComponent(
        () => import('./engine/Servers.tsx'),
        'FactoryServersPage',
      ) as RouteComponent,
    },
    {
      path: '/factory/settings',
      component: lazyRouteComponent(
        () => import('./engine/FactorySettings.tsx'),
        'FactorySettingsPage',
      ) as RouteComponent,
    },
    // A volta do Google depois de ligar o Drive (ADR 0306): a mesma página, que pega o código.
    {
      path: '/factory/settings/google-drive',
      component: lazyRouteComponent(
        () => import('./engine/FactorySettings.tsx'),
        'FactorySettingsPage',
      ) as RouteComponent,
    },
    {
      path: '/factory/connect-ai',
      component: lazyRouteComponent(
        () => import('./connect-ai/ConnectAi.tsx'),
        'ConnectAiPage',
      ) as RouteComponent,
    },
  ],
  // Públicas de propósito: quem assina ainda não tem conta (ADR 0211); a pessoa aprova a IA fora
  // da moldura, como no login (ADR 0218); o chat embutido abre dentro do sistema do cliente, com a
  // sessão dele (ADR 0250).
  publicRoutes: [
    {
      path: '/signup',
      component: lazyRouteComponent(
        () => import('./portal/Signup.tsx'),
        'SignupPage',
      ) as RouteComponent,
    },
    {
      path: '/connect-ai/authorize',
      component: lazyRouteComponent(
        () => import('./connect-ai/ConnectAiAuthorize.tsx'),
        'ConnectAiAuthorizePage',
      ) as RouteComponent,
    },
    {
      path: '/embed/chat/$systemId',
      component: lazyRouteComponent(
        () => import('./chat/ChatPages.tsx'),
        'EmbedChatPage',
      ) as RouteComponent,
    },
  ],
  banners: [TestClientBanner],
  innerScreens: [
    {
      match: (p) => /^\/my-systems\/[0-9a-f-]{36}$/i.test(p),
      parent: '/my-systems',
      key: 'factory.systemPage.crumb',
    },
    {
      match: (p) => /^\/my-systems\/interview\/[0-9a-f-]{36}$/i.test(p),
      parent: '/my-systems',
      key: 'factory.interview.crumb',
    },
    {
      match: (p) => /^\/factory\/systems\/[0-9a-f-]{36}\/as-client$/i.test(p),
      parent: '/factory/client-view',
      key: 'factory.clientView.systemCrumb',
    },
    {
      match: (p) => /^\/factory\/clients\/[0-9a-f-]{36}\/as-client$/i.test(p),
      parent: '/factory/clients',
      key: 'factory.asClient.crumb',
    },
    {
      match: (p) => /^\/factory\/clients\/[0-9a-f-]{36}$/i.test(p),
      parent: '/factory/clients',
      key: 'factory.clients.crumb',
    },
    {
      match: (p) => p === '/factory/modules/store',
      parent: '/factory/modules',
      key: 'factory.storePreview.crumb',
    },
    {
      match: (p) => /^\/factory\/systems\/[0-9a-f-]{36}$/i.test(p),
      parent: '/factory/systems',
      key: 'factory.record.crumb',
    },
  ],
  // Quem só tem permissões da área do cliente (o perfil Cliente): o Início dele é Meus sistemas.
  homeRedirect: (permissions) =>
    permissions.length > 0 && permissions.every((p) => p.startsWith('portal.'))
      ? '/my-systems'
      : null,
  // A assinatura do cliente vira uma aba de Minha conta (ADR 0139 da base).
  accountTabs: [
    {
      id: 'subscription',
      labelKey: 'factory.subscription.tab',
      component: SubscriptionSection,
      permission: 'portal.system.read',
    },
  ],
  permissionAreas: [
    {
      id: 'factory',
      key: 'factory.areas.factory',
      processes: [
        {
          id: 'factorySystems',
          key: 'factory.processes.factorySystems',
          permissions: [
            'factory.system.read',
            'factory.system.manage',
            'factory.code.ai',
            'factory.server.manage',
            'factory.catalog.manage',
          ],
        },
        {
          id: 'factoryClients',
          key: 'factory.processes.factoryClients',
          permissions: ['factory.client.read', 'factory.client.manage'],
        },
      ],
    },
    {
      id: 'portal',
      key: 'factory.areas.portal',
      processes: [
        {
          id: 'portalSystems',
          key: 'factory.processes.portalSystems',
          permissions: [
            'portal.system.read',
            'portal.interview.manage',
            'portal.change.publish',
            'portal.ai.connect',
          ],
        },
      ],
    },
  ],
};
